import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { swaggerApiService } from '../services/swaggerApi.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ApiModel } from '../classes/api_model';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { JSGenerateRSAKey } from '../services/JSGenerateRSAKey';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from "./../translate.service";
import { TokenStorageService } from '../services/token-storage.service';
import { environment } from 'src/environments/environment';
import { Subject } from 'rxjs';
import { finalize, switchMap, takeUntil } from 'rxjs/operators';
import { OAuthClientService } from '../services/oauth-client.service';

@Component({
  selector: 'app-feedback-detail',
  templateUrl: './feedback-detail.component.html',
  styleUrls: ['./feedback-detail.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FeedbackDetailComponent implements OnInit, OnDestroy {

  listFeedback: any = [];
  createFeedback = [];
  subjectId = '67e217c36c66784b1c85e626';
  typeId = '67e217c86c66784b1c85e62b';
  userId = '';
  comment = '';
  description = '';
  isLoading = false;
  isBookLoading = false;
  isFeedbackLoading = false;
  isRelatedLoading = false;
  isSubmitting = false;
  book: any;
  userInfo: any = {
    profile: '' // will hold image filename like 'lc_document/abc.jpg'
  };

  profileImage: String;
  showDropdown = false;
  hasVerifiedProfile = false;


  @Input() currentLanguage: string;

  feedbackForm: FormGroup;
  imageProfile: string | ArrayBuffer = null; // ✅ Declare imageProfile
  imagePreview: string | undefined; // To hold the preview image URL
  image: File | null = null; // To hold the file
  // Add this property to your component
  selectedBookId: string | null = null;
  eLibraryId: string;
  eLibraryName: string;
  webUrl = environment.websiteUrl;
  readonly libraryBaseUrl = `${(environment as any).apiBaseUrl || 'https://api.sathachak.com'}/library/`;
  private destroy$ = new Subject<void>();
  private bookRequestSeq = 0;
  private feedbackRequestSeq = 0;
  private relatedRequestSeq = 0;


  constructor(
    private _swaggerApiService: swaggerApiService,
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute, // <-- Add this
    private translate: TranslateService,
    public dialog: MatDialog,
    private tokenStorage: TokenStorageService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private oauthClient: OAuthClientService,
  ) {
    this.feedbackForm = this.fb.group({
      comment: ['', Validators.required],
      avatar: [null]
    });
  }

  ngOnInit() {
    if (this.tokenStorage.handleLogoutParam()) {
      this.clearExpiredLogin();
    }

    if (this.tokenStorage.isLoggedIn()) {
      const user = this.tokenStorage.getUser();
      if (user) {
        this.setProfileData(user);
      }
      this.profileInfo();
    } else {
      this.clearExpiredLogin();
    }

    this.route.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.loadLibraryDetail(params['id']);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadLibraryDetail(eLibraryId: string): void {
    if (!eLibraryId) {
      return;
    }

    this.eLibraryId = eLibraryId;
    this.selectedBookId = eLibraryId;
    ++this.relatedRequestSeq;
    this.relatedItems = [];
    this.isRelatedLoading = false;
    this.getBookDetailSecurely(eLibraryId);
    this.fetchFeedback(eLibraryId);
  }


  profileInfo() {
    this._swaggerApiService.userProfile().pipe(takeUntil(this.destroy$)).subscribe({
      next: (res) => {
        if (res !== null && res !== undefined) {
          if (this.isInvalidTokenResponse(res)) {
            this.clearExpiredLogin();
            return;
          }

          const profile = this.getProfilePayload(res);
          if (!this.hasProfileIdentity(profile)) {
            this.clearExpiredLogin();
            return;
          }

          this.setProfileData(profile);
          this.hasVerifiedProfile = true;
          this.tokenStorage.saveIsUserLogin('true');
          this.tokenStorage.saveUser(profile);
          const image = profile.profile || profile.image || profile.avatar || '';
          if (image) {
            this.tokenStorage.saveProfileImage(image);
          }
          // ✅ Store the access token safely
          if (res.accessToken) {
            this.tokenStorage.saveAccessToken(res.accessToken);
          }
          this.cdr.markForCheck();
          // console.log("API response:", this.userInfo);
        }
      },
      error: (error) => {
        if (this.isInvalidTokenResponse(error && (error.error || error))) {
          this.clearExpiredLogin();
          return;
        }

        console.error("API error:", error);
      },
    });
  }

  private getProfilePayload(response: any): any {
    return response && (response.user || response.data || response.item || response.profileInfo)
      ? (response.user || response.data || response.item || response.profileInfo)
      : response;
  }

  private isInvalidTokenResponse(response: any): boolean {
    const message = response && response.message ? response.message.toString().toLowerCase() : '';
    return message.indexOf('invalid') !== -1 && message.indexOf('token') !== -1
      || message.indexOf('expired') !== -1 && message.indexOf('token') !== -1;
  }

  private hasProfileIdentity(profile: any): boolean {
    return !!(profile && (
      profile.fullName ||
      profile.fullname ||
      profile.name ||
      profile.username ||
      profile.email ||
      profile.mail ||
      profile.profile ||
      profile.image ||
      profile.avatar ||
      profile._id ||
      profile.id ||
      profile.userId
    ));
  }

  private setProfileData(profile: any): void {
    if (!profile) {
      return;
    }

    this.userInfo = { ...this.userInfo, ...profile };
    this.userId = this.getProfileId(profile) || this.userId;
    this.profileImage = profile.profile || profile.image || profile.avatar || this.profileImage || '';
    this.cdr.markForCheck();
  }

  private getProfileId(profile: any): string {
    return profile && (profile._id || profile.id || profile.userId)
      ? (profile._id || profile.id || profile.userId)
      : '';
  }

  get canCreateFeedback(): boolean {
    return this.hasVerifiedProfile && this.tokenStorage.hasToken() && !!this.getProfileId(this.userInfo);
  }

  private clearExpiredLogin(): void {
    this.tokenStorage.clearSession();
    this.userInfo = { profile: '' };
    this.userId = '';
    this.hasVerifiedProfile = false;
    this.profileImage = '';
    this.showDropdown = false;
    this.cdr.markForCheck();
    console.warn('Profile token is invalid or expired. Please login again.');
  }

  getBookDetailSecurely(eLibId: string): void {
    const requestSeq = ++this.bookRequestSeq;
    this.isBookLoading = true;
    this.isLoading = true;
    const key = new JSGenerateRSAKey();
    let objIT = new ApiModel();
    objIT.public_key = key.getPublicKey();
    objIT.id = eLibId;
    objIT.modified_date = new Date().toISOString();

    this._swaggerApiService.postGetLibraryId(objIT).pipe(
      switchMap((encrypted: string) => {
        let objDecrypt = new ApiModel();
        objDecrypt.private_key = key.getPrivateKey();
        objDecrypt.encryptedData = encrypted;
        return this._swaggerApiService.postDecrypted(objDecrypt);
      }),
      finalize(() => {
        if (requestSeq === this.bookRequestSeq) {
          this.isBookLoading = false;
          this.isLoading = this.isFeedbackLoading || this.isRelatedLoading;
          this.cdr.markForCheck();
        }
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (res) => {
        if (requestSeq !== this.bookRequestSeq) {
          return;
        }
        if (res.statusCode === 200 && res.item) {
          this.book = res.item;
          this.selectedBookId = this.book._id;
          this.eLibraryId = this.book._id;
          // this.subjectId = this.book._id || '';
          // this.typeId = this.book.subCategory || this.book.category || this.book.typeId || '';
          this.eLibraryName = this.book.titleKh;
          this.fetchRelatedBooks();
        } else {
          this.book = null;
          this._snackBar.open('រកមិនឃើញឯកសារ។', 'OK', { duration: 2000 });
        }
        this.cdr.markForCheck();
      },
      error: (err) => {
        console.error('Fetch error:', err);
        this._snackBar.open('មានបញ្ហាក្នុងការទាញយកព័ត៌មានឯកសារ។', 'OK', { duration: 2000 });
      }
    });
  }


  // Method to fetch feedback
  fetchFeedback(eLibId: string): void {
    const requestSeq = ++this.feedbackRequestSeq;
    this.isFeedbackLoading = true;
    this.isLoading = true;
    this._swaggerApiService.getFeedback(eLibId).pipe(
      finalize(() => {
        if (requestSeq === this.feedbackRequestSeq) {
          this.isFeedbackLoading = false;
          this.isLoading = this.isBookLoading || this.isRelatedLoading;
          this.cdr.markForCheck();
        }
      }),
      takeUntil(this.destroy$)
    ).subscribe(
      (data: any) => {
        if (requestSeq !== this.feedbackRequestSeq) {
          return;
        }
        this.listFeedback = this.parseFeedbackResponse(data);
        this.cdr.markForCheck();
      },
      (error) => {
        console.error("Error fetching feedback:", error);
        this.listFeedback = [];
        this._snackBar.open('មានបញ្ហាក្នុងការទាញយកមតិយោបល់។', 'OK', { duration: 2000 });
      }
    );
  }

  private parseFeedbackResponse(data: any): any[] {
    if (Array.isArray(data)) {
      return data;
    }

    if (typeof data === 'string') {
      try {
        const parsed = JSON.parse(data);
        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        console.error('Invalid feedback response:', error);
        return [];
      }
    }

    return data && Array.isArray(data.item) ? data.item : [];
  }
  convertYear(year) {
    let yearStr = year;
    if (this.currentLanguage == 'km') {
      yearStr = this.toKhmerNumber(year);
    }
    return yearStr;
  }
  toKhmerNumber(str) {
    if (str) {
      var khNumber = str.toString();

      khNumber = khNumber.replace(/0/g, '០')
      khNumber = khNumber.replace(/1/g, '១')
      khNumber = khNumber.replace(/2/g, '២')
      khNumber = khNumber.replace(/3/g, "៣")
      khNumber = khNumber.replace(/4/g, "៤")
      khNumber = khNumber.replace(/5/g, "៥")
      khNumber = khNumber.replace(/6/g, "៦")
      khNumber = khNumber.replace(/7/g, "៧")
      khNumber = khNumber.replace(/8/g, "៨")
      khNumber = khNumber.replace(/9/g, "៩")
    }

    return khNumber
  }
  submitFeedback() {
    if (!this.canCreateFeedback) {
      this._snackBar.open("សូមចូលប្រើប្រាស់ជាមុនសិន!", "OK", { duration: 2000 });
      return;
    }

    // this.subjectId = this.subjectId || this.eLibraryId || (this.book && this.book._id) || '';
    // this.typeId = this.typeId || (this.book && (this.book.subCategory || this.book.category || this.book.typeId)) || '';
    this.userId = this.userId || this.getProfileId(this.userInfo);

    if (!this.subjectId || !this.typeId || !this.userId) {
      this._snackBar.open("មិនអាចផ្ញើមតិយោបល់បានទេ។ សូមចូលប្រើប្រាស់ម្ដងទៀត។", "OK", { duration: 2500 });
      return;
    }

    const comment = this.feedbackForm.get('comment');
    if (!comment || !comment.value) {
      alert("សូមបញ្ចូលមតិយោបល់!");
      return;
    }

    let formData = new FormData();
    formData.append('subjectId', this.subjectId);
    formData.append('typeId', this.typeId);
    formData.append('userId', this.userId);
    formData.append('comment', comment.value); // Access comment value without optional chaining
    // formData.append('description', "this.description");
    if (this.image) {
      formData.append('image', this.image); // Attach file if exists
    }
    // console.log("Formdata = ", formData);


    formData.append('description', JSON.stringify({ id: this.eLibraryId, title: this.eLibraryName }));

    this.isSubmitting = true;
    this._swaggerApiService.postFeedback(formData).pipe(
      finalize(() => {
        this.isSubmitting = false;
        this.cdr.markForCheck();
      }),
      takeUntil(this.destroy$)
    ).subscribe(
      (data) => {
        // console.log("Feedback submitted successfully: ", data);
        this._snackBar.open("ផ្ញើមតិយោបល់ដោយជោគជ័យ!", "OK", { duration: 2000 });
        this.resetForm(); // Reset the form after submission
        this.fetchFeedback(this.eLibraryId);
      },
      (error) => {
        console.error("Error submitting feedback:", error);
        this._snackBar.open("មានបញ្ហាក្នុងការផ្ញើមតិយោបល់!", "OK", { duration: 2000 });
      }
    );
  }

  resetForm() {
    this.feedbackForm.reset();
    this.imagePreview = undefined;
    this.image = null;
  }

  onMenuClicked(index) {
    this.router.navigate(['contents', index, this.currentLanguage]);
  }
  onHome() {
    this.router.navigate([this.currentLanguage]);
  }

  openLogin(event: Event, book: any) {
    event.preventDefault();
    event.stopPropagation();
    const isLogin = this.tokenStorage.getIsUserLogin();
    const tokenValue = this.tokenStorage.getAccessToken();

    if (isLogin === 'true') {
      console.log("✅ User is already logged in.");
      window.open(this.buildReaderUrl(book._id, true), '_blank', 'noopener');

    } else {
      console.log("🔐 User not logged in. Opening login dialog...");
      this.redirectToLogin(`${this.webUrl}/contents/2/km`);
    }
  }

  private buildReaderUrl(bookId: string, includeAuth = false): string {
    const readerUrl = new URL('/docs/' + encodeURIComponent(bookId), environment.readerUrl);
    if (includeAuth) {
      const token = this.tokenStorage.getAccessToken();
      const refreshToken = this.tokenStorage.getRefreshToken();
      const sessionId = this.tokenStorage.getSessionId();

      if (token) {
        readerUrl.searchParams.set('token', token);
      }
      if (refreshToken) {
        readerUrl.searchParams.set('refreshToken', refreshToken);
      }
      if (sessionId) {
        readerUrl.searchParams.set('sessionId', sessionId);
      }
    }

    return readerUrl.toString();
  }



  goToELibrary() {
    this.router.navigate(['contents/2/km']);
  }


  showPreview(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      this.image = input.files[0];
      const reader = new FileReader();
      reader.onload = () => {
        this.imagePreview = reader.result as string;
        this.cdr.markForCheck();
      };
      reader.readAsDataURL(this.image);
    }
  }

  openProfile(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    const isLogin = this.tokenStorage.getIsUserLogin();
    const hasToken = this.tokenStorage.hasToken();
    // console.log("Login status: ", isLogin);

    if (isLogin === 'true' || hasToken) {
      console.log("✅ User is already logged in.");
      this.dialog.closeAll();
      this.navigateToProfile();
    } else {
      this.showDropdown = false;

      console.log("🔐 User not logged in. Opening login dialog...");
      this.redirectToLogin();

    }
  }

  loginToCreateFeedback(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    let returnUrl = window.location.pathname + window.location.search;
    if (!returnUrl || returnUrl.includes('http:') || returnUrl.includes('/auth/callback')) {
      returnUrl = '/contents/2/km';
    }
    this.redirectToLogin(returnUrl);
  }

  private redirectToLogin(returnUrl: string = '/contents/2/km'): void {
    let safeReturnUrl = returnUrl;
    if (!safeReturnUrl || safeReturnUrl.includes('http:') || safeReturnUrl.includes('/auth/callback')) {
      safeReturnUrl = '/contents/2/km';
    }
    const callbackUrl = `${window.location.origin}/auth/callback?returnUrl=${encodeURIComponent(safeReturnUrl)}`;
    const loginUrl = new URL(environment.ssoLoginUrl);
    loginUrl.searchParams.set('redirect_uri', callbackUrl);
    window.location.href = loginUrl.toString();
  }

  navigateToProfile(): void {
    const token = this.tokenStorage.getAccessToken();
    if (!token) {
      console.warn('No auth token found');
      return;
    }

    window.location.href = this.getSsoDashboardUrl();
  }

  private getSsoDashboardUrl(): string {
    const defaultDashboard = (environment as any).ssoDashboardUrl
      || (environment.ssoLoginUrl ? new URL('/dashboard', environment.ssoLoginUrl).toString() : (environment.readerUrl || 'http://localhost:4300'));

    try {
      const baseUrl = (environment as any).ssoDashboardUrl || environment.ssoLoginUrl;
      const url = new URL('/verifying', baseUrl);
      url.searchParams.set('redirect', '/dashboard');
      url.searchParams.set('post_logout_redirect', `${window.location.origin}/?logout=1`);

      const token = this.tokenStorage.getAccessToken();
      const refreshToken = this.tokenStorage.getRefreshToken();
      const sessionId = this.tokenStorage.getSessionId();
      const userId = this.tokenStorage.getUserId();

      if (token) {
        url.searchParams.set('token', token);
      }
      if (refreshToken) {
        url.searchParams.set('refreshToken', refreshToken);
      }
      if (sessionId) {
        url.searchParams.set('sessionId', sessionId);
      }
      if (userId) {
        url.searchParams.set('userId', userId);
      }

      return url.toString();
    } catch (error) {
      return defaultDashboard;
    }
  }



  onErrorFormat(arg0: string, arg1: string) {
    throw new Error('Method not implemented.');
  }

  relatedItems: any[] = [];
  relatedSkeletonList = [1, 2, 3];
  fetchRelatedBooks(): void {
    const requestSeq = ++this.relatedRequestSeq;
    this.relatedItems = [];
    this.isRelatedLoading = true;
    this.isLoading = true;
    const key = new JSGenerateRSAKey();
    const request = new ApiModel();
    request.public_key = key.getPublicKey();
    request.modified_date = new Date().toISOString();
    request.limit = 100;

    // Use the same grouped library list as the library page.
    this._swaggerApiService.postELibraryTypes(request).pipe(
      switchMap((encrypted: string) => {
        const decryptRequest = new ApiModel();
        decryptRequest.private_key = key.getPrivateKey();
        decryptRequest.encryptedData = encrypted;
        return this._swaggerApiService.postDecrypted(decryptRequest);
      }),
      finalize(() => {
        if (requestSeq === this.relatedRequestSeq) {
          this.isRelatedLoading = false;
          this.isLoading = this.isBookLoading || this.isFeedbackLoading;
          this.cdr.markForCheck();
        }
      }),
      takeUntil(this.destroy$)
    ).subscribe(
      data => {
        if (requestSeq !== this.relatedRequestSeq) {
          return;
        }
        const groups = data && data.statusCode === 200 && Array.isArray(data.item) ? data.item : [];
        const seen = new Set<string>();
        this.relatedItems = groups.reduce((items, group) => {
          if (!group || group.title === 'កម្មវិធីលើទូរសព្ទ' || !Array.isArray(group.item)) {
            return items;
          }
          group.item.forEach(book => {
            if (book && book._id && book._id !== this.eLibraryId && !seen.has(book._id)) {
              seen.add(book._id);
              items.push(book);
            }
          });
          return items;
        }, []);
        this.cdr.markForCheck();
      },
      error => {
        if (requestSeq !== this.relatedRequestSeq) {
          return;
        }
        console.error('API error:', error);
        this.relatedItems = [];
        this.cdr.markForCheck();
      }
    );
  }

  convertDate(date) {
    let dateList, dateStr = '';
    let dateLabel = ['ថ្ងៃទី', 'ខែ', 'ឆ្នាំ'];
    dateList = date.split('-');
    dateList[1] = this.toKhmerMonth(dateList[1]);
    if (this.currentLanguage == 'km') {
      for (let i = 0; i < dateList.length; i++) {
        dateList[i] = this.toKhmerNumber(dateList[i]);
        dateStr += dateLabel[i] + dateList[i] + ' ';
      }
    } else {
      for (let i = 0; i < dateList.length; i++) {
        dateStr += dateList[i] + ' ';
      }
    }
    return dateStr;
  }

  onBookClick(eLibId: string) {
    this.router.navigate(['/detail', eLibId]);
  }

  get profileImageUrl(): string {
    const profile = (this.profileImage || this.userInfo && (this.userInfo.profile || this.userInfo.image || this.userInfo.avatar) || '').toString();
    if (!profile) {
      return './assets/user.png';
    }

    return profile.indexOf('https://lh3.googleusercontent.com') !== -1 || profile.indexOf('http') === 0
      ? profile
      : this.libraryBaseUrl + profile;
  }

  get displayUserName(): string {
    const firstName = this.userInfo && (this.userInfo.firstName || this.userInfo.firstname) || '';
    const lastName = this.userInfo && (this.userInfo.lastName || this.userInfo.lastname) || '';
    const splitName = `${firstName} ${lastName}`.trim();

    return this.userInfo && (this.userInfo.fullName || this.userInfo.fullname || this.userInfo.name || this.userInfo.username || splitName)
      ? (this.userInfo.fullName || this.userInfo.fullname || this.userInfo.name || this.userInfo.username || splitName)
      : 'អ្នកប្រើប្រាស់';
  }

  get displayUserEmail(): string {
    return this.userInfo && (this.userInfo.email || this.userInfo.mail) ? (this.userInfo.email || this.userInfo.mail) : '';
  }

  getFeedbackUserName(feedback: any): string {
    const user = feedback && feedback.user ? feedback.user : {};
    return user.fullName || user.fullname || user.name || user.username || 'អ្នកប្រើប្រាស់';
  }

  getFeedbackUserEmail(feedback: any): string {
    return feedback && feedback.user && feedback.user.email ? feedback.user.email : '';
  }

  trackByFeedbackId(index: number, feedback: any): string {
    return feedback && (feedback._id || feedback.id || feedback.createdDate)
      ? (feedback._id || feedback.id || feedback.createdDate)
      : String(index);
  }

  trackByBookId(index: number, book: any): string {
    return book && book._id ? book._id : String(index);
  }

  toKhmerMonth(str) {
    var khNumber = str.toString();

    khNumber = khNumber.replace(/01/g, this.translate.data.MONTH_01)
    khNumber = khNumber.replace(/02/g, this.translate.data.MONTH_02)
    khNumber = khNumber.replace(/03/g, this.translate.data.MONTH_03)
    khNumber = khNumber.replace(/04/g, this.translate.data.MONTH_04)
    khNumber = khNumber.replace(/05/g, this.translate.data.MONTH_05)
    khNumber = khNumber.replace(/06/g, this.translate.data.MONTH_06)
    khNumber = khNumber.replace(/07/g, this.translate.data.MONTH_07)
    khNumber = khNumber.replace(/08/g, this.translate.data.MONTH_08)
    khNumber = khNumber.replace(/09/g, this.translate.data.MONTH_09)
    khNumber = khNumber.replace(/10/g, this.translate.data.MONTH_10)
    khNumber = khNumber.replace(/11/g, this.translate.data.MONTH_11)
    khNumber = khNumber.replace(/12/g, this.translate.data.MONTH_12)
    return khNumber
  }


}
